import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaAward, FaUsers, FaFolder, FaUniversity } from 'react-icons/fa';
import ResumeModal from './ResumeModal';
import './About.css';

// Animated counter hook
const useCounter = (target, duration = 1500, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatItem = ({ icon: Icon, target, suffix, text, delay }) => {
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const count = useCounter(target, 1500, started);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      className="stat-item"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
    >
      <div className="stat-icon"><Icon /></div>
      <div className="stat-content">
        <h3 className="stat-number">
          {target > 0 ? `${count}${suffix}` : suffix}
        </h3>
        <p className="stat-text">{text}</p>
      </div>
    </motion.div>
  );
};

const About = () => {
  const [showResume, setShowResume] = useState(false);

  const stats = [
    { icon: FaAward, target: 1, suffix: '+', text: 'Years Learning' },
    { icon: FaFolder, target: 5, suffix: '+', text: 'Projects Completed' },
    { icon: FaUsers, target: 0, suffix: 'Co-founder', text: 'AGR Creative Tech Studio' },
    { icon: FaUniversity, target: 1, suffix: '', text: 'Institution' },
  ];

  return (
    <>
      <section className="about section" id="about">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">About</h2>
            <p className="section-subtitle">My Introduction</p>
          </motion.div>

          <div className="about-content">
            <motion.div
              className="about-image"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="about-img-container">
                <img
                  src="/lakshmanan-profile.jpg.jpg"
                  alt="Lakshmanan R"
                  className="about-img"
                />
                <div className="about-img-overlay">
                  <span>Lakshmanan R</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <p className="about-description">
                I am a passionate student and Co-founder of AGR Creative Tech Studio, currently pursuing
                B.Tech in Computer Science &amp; Engineering with specialization in Artificial Intelligence and
                Data Science at Hindustan Institute of Technology &amp; Science. I am learning and developing
                skills in web development, AI, and data science through academic projects and personal
                learning. As a co-founder, I bring entrepreneurial mindset and leadership skills to every
                project. I am dedicated to learning and growing in the field of technology and am eager to
                contribute my enthusiasm and fresh perspective to innovative teams. If you're interested in
                discussing opportunities, collaboration, or need assistance, please don't hesitate to reach out via email.
              </p>

              <div className="about-stats">
                {stats.map((stat, index) => (
                  <StatItem
                    key={index}
                    icon={stat.icon}
                    target={stat.target}
                    suffix={stat.suffix}
                    text={stat.text}
                    delay={index * 0.1}
                  />
                ))}
              </div>

              <motion.div
                className="about-actions"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <button className="btn" onClick={() => setShowResume(true)}>
                  <FaDownload />
                  View Resume
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <ResumeModal isOpen={showResume} onClose={() => setShowResume(false)} />
    </>
  );
};

export default About;